import { object } from 'zod/mini';
import type { infer as Infer } from 'zod/mini';
import {
    choiceField,
    DASHBOARD_VALUE_FORMATS,
    describeField,
    measureFields,
    repeated,
    switchField,
    textField,
    withDefaults,
} from '@h20-shared/dashboards';
import {
    KPI_CARD_ALIGNMENTS,
    KPI_CARD_LABEL_EMPHASES,
    KPI_CARD_VALUE_EMPHASES,
} from '@h20-shared/pulse-components/pulse-kpi-card';
import {
    KPI_DEFAULT_CARDS,
    KPI_DIRECTIONS,
    KPI_DISPLAYS,
    KPI_ICON_COLORS,
    KPI_ICON_NAMES,
    KPI_MAX_CARDS,
} from './dashboard-kpi.config';

const KPI_MEASURE_SCHEMA = object(
    measureFields(
        'Dashboard.KpiSettings.ValueColumn',
        'Dashboard.KpiSettings.Aggregate',
        'Dashboard.KpiSettings.Where'
    )
);

const KPI_CARD_SCHEMA = object({
    label: textField('Dashboard.KpiSettings.Label'),
    tooltip: textField('Dashboard.KpiSettings.Tooltip'),
    caption: textField('Dashboard.KpiSettings.Caption'),
    numerator: describeField(KPI_MEASURE_SCHEMA, { inline: true }),
    format: choiceField(DASHBOARD_VALUE_FORMATS, 'Dashboard.KpiSettings.Format'),
    icon: describeField(
        object({
            enabled: switchField('Dashboard.KpiSettings.Icon'),
            name: choiceField(KPI_ICON_NAMES, 'Dashboard.KpiSettings.IconSymbol'),
            color: choiceField(KPI_ICON_COLORS, 'Dashboard.KpiSettings.IconColor'),
        }),
        { label: 'Dashboard.KpiSettings.Icon' }
    ),
    denominator: describeField(
        object({
            ...measureFields(
                'Dashboard.KpiSettings.DenominatorColumn',
                'Dashboard.KpiSettings.DenominatorAggregate',
                'Dashboard.KpiSettings.DenominatorWhere'
            ),
            enabled: switchField('Dashboard.KpiSettings.Denominator'),
            display: choiceField(KPI_DISPLAYS, 'Dashboard.KpiSettings.Display'),
        }),
        { label: 'Dashboard.KpiSettings.Denominator' }
    ),
    link: describeField(
        object({
            enabled: switchField('Dashboard.KpiSettings.Link'),
            label: textField('Dashboard.KpiSettings.LinkLabel'),
            href: textField('Dashboard.KpiSettings.LinkHref'),
            wholeCard: switchField('Dashboard.KpiSettings.LinkWholeCard'),
        }),
        { label: 'Dashboard.KpiSettings.Link' }
    ),
});

const KPI_PRESENTATION_SCHEMA = describeField(
    object({
        valueEmphasis: choiceField(KPI_CARD_VALUE_EMPHASES, 'Dashboard.KpiSettings.ValueEmphasis'),
        alignment: choiceField(KPI_CARD_ALIGNMENTS, 'Dashboard.KpiSettings.ContentAlignment'),
        labelEmphasis: choiceField(KPI_CARD_LABEL_EMPHASES, 'Dashboard.KpiSettings.LabelEmphasis'),
    }),
    { label: 'Dashboard.KpiSettings.Presentation' }
);

export const KPI_CONFIG_SCHEMA = withDefaults(
    object({
        direction: choiceField(KPI_DIRECTIONS, 'Dashboard.KpiSettings.Direction'),
        presentation: KPI_PRESENTATION_SCHEMA,
        cards: repeated(KPI_CARD_SCHEMA, {
            max: KPI_MAX_CARDS,
            fallback: KPI_DEFAULT_CARDS,
            label: 'Dashboard.KpiSettings.Cards',
            itemLabel: 'Dashboard.KpiSettings.Card',
            countLabel: 'Dashboard.KpiSettings.CardCount',
        }),
    })
);

export type KpiMeasure = Infer<typeof KPI_MEASURE_SCHEMA>;
export type KpiCardConfig = Infer<typeof KPI_CARD_SCHEMA>;
export type KpiPresentation = Infer<typeof KPI_PRESENTATION_SCHEMA>;
export type KpiWidgetConfig = Infer<typeof KPI_CONFIG_SCHEMA>;

export const KPI_DEFAULT_PRESENTATION: KpiPresentation = withDefaults(
    KPI_PRESENTATION_SCHEMA
).parse({});
